import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import {
	archiveAiManagedObject,
	createAiManagedObject,
	fetchAiManagedObject,
	reactivateAiManagedObject,
	updateAiManagedObject,
} from '@atlassian/ai-managed-objects/api';
import type {
	AiManagedObject,
	AiManagedObjectCreateInput,
	AiManagedObjectStatus,
	AiManagedObjectUpdateInput,
} from '@atlassian/ai-managed-objects/types';

import {
	setAgentManagedExtensionMeta,
	setNodeAiManagedObject,
} from './editor-commands/agent-managed-object-node';
import {
	cacheAiManagedObject as cacheAiManagedObjectCommand,
	failAiManagedObjectLoad,
	getCacheAiManagedObjectMeta,
	startAiManagedObjectLoad,
} from './editor-commands/configure-popup';
import {
	type AgentManagedExtensionMeta,
	type AgentManagedExtensionMetaAction,
	getCachedAiManagedObjectFromState,
} from './pm-plugins/state';

type AgentManagedExtensionMetaInput =
	| AgentManagedExtensionMeta
	| (() => AgentManagedExtensionMeta | undefined);

export const getCachedAiManagedObject = (
	editorView: EditorView,
	aimoId: string,
): AiManagedObject | undefined => getCachedAiManagedObjectFromState(editorView.state, aimoId);

export const cacheAiManagedObject = (
	editorView: EditorView,
	aimo: AiManagedObject,
	localId?: string,
): void => {
	const tr = cacheAiManagedObjectCommand(aimo, localId)({ tr: editorView.state.tr });
	if (tr) {
		editorView.dispatch(tr);
	}
};

const isMetaActionArray = (
	meta: AgentManagedExtensionMeta,
): meta is readonly AgentManagedExtensionMetaAction[] => Array.isArray(meta);

const toMetaActions = (
	meta: AgentManagedExtensionMeta | undefined,
): readonly AgentManagedExtensionMetaAction[] | undefined => {
	if (!meta) {
		return undefined;
	}
	return isMetaActionArray(meta) ? meta : [meta];
};

const persistMutatedAiManagedObject = (
	editorView: EditorView,
	aimo: AiManagedObject,
	localId?: string,
	meta?: AgentManagedExtensionMetaInput,
): void => {
	const tr = editorView.state.tr;
	setNodeAiManagedObject(editorView.state, tr, aimo, localId);

	const metaActions = toMetaActions(typeof meta === 'function' ? meta() : meta);
	setAgentManagedExtensionMeta(
		tr,
		metaActions
			? [getCacheAiManagedObjectMeta(aimo, localId), ...metaActions]
			: getCacheAiManagedObjectMeta(aimo, localId),
	);
	editorView.dispatch(tr);
};

const dispatchAiManagedObjectLoadStarted = (
	editorView: EditorView,
	aimoId: string,
	localId?: string,
): void => {
	const tr = startAiManagedObjectLoad(aimoId, localId)({ tr: editorView.state.tr });
	if (tr) {
		editorView.dispatch(tr);
	}
};

const dispatchAiManagedObjectLoadFailed = (
	editorView: EditorView,
	aimoId: string,
	localId?: string,
): void => {
	const tr = failAiManagedObjectLoad(aimoId, localId)({ tr: editorView.state.tr });
	if (tr) {
		editorView.dispatch(tr);
	}
};

export const getAiManagedObjectWithCache = async ({
	aimoId,
	cachedAimo,
	editorView,
	localId,
}: {
	aimoId: string;
	cachedAimo?: AiManagedObject;
	editorView: EditorView;
	localId?: string;
}): Promise<AiManagedObject | null> => {
	const existingAimo = cachedAimo ?? getCachedAiManagedObject(editorView, aimoId);
	if (existingAimo) {
		if (localId) {
			cacheAiManagedObject(editorView, existingAimo, localId);
		}
		return existingAimo;
	}

	dispatchAiManagedObjectLoadStarted(editorView, aimoId, localId);

	try {
		const fetchedAimo = await fetchAiManagedObject(aimoId);
		if (fetchedAimo) {
			cacheAiManagedObject(editorView, fetchedAimo, localId);
			return fetchedAimo;
		}
	} catch {
		dispatchAiManagedObjectLoadFailed(editorView, aimoId, localId);
		throw new Error('Failed to load AI-managed object');
	}

	dispatchAiManagedObjectLoadFailed(editorView, aimoId, localId);
	return null;
};

export const createAiManagedObjectWithCache = async (
	editorView: EditorView,
	input: AiManagedObjectCreateInput,
	localId?: string,
	meta?: AgentManagedExtensionMetaInput,
): Promise<AiManagedObject> => {
	const aimo = await createAiManagedObject(input);
	persistMutatedAiManagedObject(editorView, aimo, localId, meta);
	return aimo;
};

export const updateAiManagedObjectWithCache = async (
	editorView: EditorView,
	input: AiManagedObjectUpdateInput,
	localId?: string,
	meta?: AgentManagedExtensionMetaInput,
): Promise<AiManagedObject> => {
	const aimo = await updateAiManagedObject(input);
	persistMutatedAiManagedObject(editorView, aimo, localId, meta);
	return aimo;
};

const cacheAiManagedObjectStatus = (
	editorView: EditorView,
	aimoId: string,
	status: AiManagedObjectStatus,
	localId?: string,
): void => {
	const cachedAimo = getCachedAiManagedObject(editorView, aimoId);
	if (!cachedAimo) {
		return;
	}

	// Archive/reactivate mutations return a boolean, so preserve the cached
	// object and only reflect the lifecycle status that changed on the backend.
	cacheAiManagedObject(editorView, { ...cachedAimo, status }, localId);
};

export const archiveAiManagedObjectWithCache = async (
	editorView: EditorView,
	aimoId: string,
	localId?: string,
): Promise<void> => {
	await archiveAiManagedObject(aimoId);
	cacheAiManagedObjectStatus(editorView, aimoId, 'ARCHIVED', localId);
};

export const reactivateAiManagedObjectWithCache = async (
	editorView: EditorView,
	aimoId: string,
	localId?: string,
): Promise<void> => {
	await reactivateAiManagedObject(aimoId);
	cacheAiManagedObjectStatus(editorView, aimoId, 'ACTIVE', localId);
};
